import type { ReactNode } from 'react';
import { PageLoader } from '@customerio/pluma-components/react';
import { PlanGuard } from '@/components/guards';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { BUILDER_PLAN_SLUG } from '@/services/auth/plan-slugs';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { generatePath } from '@/services/router/generate-path';
import { SafeNavigate } from '@/services/router/safe-navigate';

const LOADING_LABEL = 'Loading integration…';

type ConnectorRouteViewProps = {
  /** The connector's rollout flag, already read by the caller. */
  isMigrated: boolean;
  /** The `journeys` twin of this URL, which the embedded Ember app serves. */
  legacyHref: string | null;
  /**
   * Receives the resolved workspace id. A function rather than an element so a
   * connector's own gate can build a redirect from the id the shell waited for,
   * instead of re-deriving one from route params that read `last`.
   */
  children: (workspaceId: string) => ReactNode;
};

/**
 * The shell every migrated `/integrations/*` connector page renders inside.
 *
 * Flag on → the page renders behind the two gates Ember's `integrations` root
 * route applied to everything nested under it: Builder-plan accounts go to the
 * available-plans page, and an account without `journeys/imports/view` is sent
 * to the workspace root. A connector with a gate of its own nests a second
 * `ProtectedRoute` inside this one, the way its Ember route nested a second
 * `beforeModel`.
 *
 * Flag off → the migrated URL has no Ember counterpart, because Ember only
 * knows the `journeys` form. Rendering the embedded host here would 404 inside
 * Ember, so this hands back to the twin that Ember does serve.
 */
export function ConnectorRouteView({ isMigrated, legacyHref, children }: ConnectorRouteViewProps) {
  const workspace = useCurrentWorkspace();

  if (!isMigrated) {
    if (legacyHref == null) {
      return <PageLoader label={LOADING_LABEL} />;
    }
    return <SafeNavigate to={legacyHref} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  // Gate on the resolved workspace, not the URL segment: connector pages read the workspace's own
  // fields, and the `/workspaces/last` sentinel leaves route params stale until it resolves.
  if (workspace == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <ProtectedRoute
        permission="journeys/imports/view"
        redirectTo={generatePath('/workspaces/:workspaceId', { workspaceId: String(workspace.id) })}
      >
        {children(String(workspace.id))}
      </ProtectedRoute>
    </PlanGuard>
  );
}
